import { useState, useEffect } from 'react'
import { getPushVapidKey, savePushSubscription, deletePushSubscription } from './api'

type State = 'loading' | 'unsupported' | 'denied' | 'subscribed' | 'unsubscribed'

export function usePushSubscription() {
  const [state, setState] = useState<State>('loading')

  useEffect(() => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      setState('unsupported')
      return
    }
    navigator.serviceWorker.ready.then(async (reg) => {
      const sub = await reg.pushManager.getSubscription()
      if (sub) setState('subscribed')
      else if (Notification.permission === 'denied') setState('denied')
      else setState('unsubscribed')
    })
  }, [])

  async function subscribe() {
    setState('loading')
    try {
      const permission = await Notification.requestPermission()
      if (permission !== 'granted') {
        setState(permission === 'denied' ? 'denied' : 'unsubscribed')
        return
      }
      const { publicKey } = await getPushVapidKey()
      const reg = await navigator.serviceWorker.register('/sw.js')
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly:      true,
        applicationServerKey: publicKey,
      })
      await savePushSubscription(sub.toJSON())
      setState('subscribed')
    } catch (err) {
      console.error('Push subscribe failed:', err)
      setState(Notification.permission === 'denied' ? 'denied' : 'unsubscribed')
    }
  }

  async function test() {
    const permission = await Notification.requestPermission()
    if (permission !== 'granted') {
      throw new Error('Notifications aren’t allowed in this browser yet.')
    }
    const reg = await navigator.serviceWorker.ready
    try {
      await reg.showNotification('Tutor CRM', {
        body: 'Test notification — if you can see this, notifications work.',
        tag:  'test',
      })
    } catch {
      throw new Error(
        'Your browser allows notifications but your operating system is blocking ' +
        'them. Turn on notifications for your browser in your OS settings (and ' +
        'switch off Do Not Disturb / Focus).',
      )
    }
  }

  async function unsubscribe() {
    setState('loading')
    try {
      const reg = await navigator.serviceWorker.ready
      const sub = await reg.pushManager.getSubscription()
      await sub?.unsubscribe()
      await deletePushSubscription()
      setState('unsubscribed')
    } catch (err) {
      console.error('Push unsubscribe failed:', err)
      setState('subscribed')
    }
  }

  return { state, subscribe, unsubscribe, test }
}
